<template>
  <ValidationProvider
      v-slot="{ errors}"
      :class="$attrs.class"
      :rules="customRules"
      class="form-group w-full" tag="div"
  >
    <label v-if="label" :class="labelClass" class="block text-base font-normal mb-1">{{ label }}<b
        v-if="isRequired" class="ml-1 text-red-600 font-bold">*</b></label>
    <div class="relative input-group h-[42px]">
      <div v-if="leftIcon" class="icon-left"><i :class="leftIcon"></i></div>
      <div v-else class="icon-left z-10"></div>
      <date-range-picker
          v-model="dateRange"
          :appendToBody="true"
          :autoApply="true"
          :controlContainerClass="controlContainerClass"
          :date-format="dateFormatFn"
          :disabled="disabled"
          :linkedCalendars="false"
          :locale-data="localeData"
          :opens="opens"
          :ranges="false"
          :showDropdowns="true"
          :showWeekNumbers="false"
          :singleDatePicker="singleDatePicker"
          :time-picker="false"
          :timePicker="false"
          :timePicker24Hour="true"
          v-bind="$attrs.input"
      >
        <template v-slot:input="pickerProps">
          <i class="glyphicon glyphicon-calendar fa fa-calendar"></i>&nbsp;
          <span :class="placeHolderClass">{{ rangeText }}</span>
          <b class="caret"></b>
        </template>
      </date-range-picker>

      <div v-if="rightIcon" class="icon-right"><i :class="rightIcon" @click="$emit('rightIconClick')"></i></div>
      <slot name="right-icon"></slot>
      <div class="dropdown relative"></div>
    </div>
    <span class="text-xs text-red-600">{{ errors[0] }}</span>
  </ValidationProvider>
</template>

<script>
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import dayjs from "@/helpers/dayjs";

export default {
  name: "DatePicker",
  components: {DateRangePicker},
  props: {
    value: [Array, String, Object, Date],
    label: String,
    leftIcon: String,
    rightIcon: String,
    labelClass: [String, Array],
    inputClass: [String, Array],
    rules: String,
    name: String,
    format: {
      type: String,
      default: "dd-mm-yyyy"
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    formatOnFirst: {
      type: Boolean,
      default: true,
    },
    dateFormat: {
      type: Function,
    },
    highlightDates: {
      type: Array,
      default: () => [],
    },
    opens: {
      type: String,
      default: "left"
    },
    placeholder: {
      type: String,
      default: "Chọn ngày",
    }
  },
  data: () => ({
    singleDatePicker: true,
    placeHolderClass: "text-[#00000040]",
    controlContainerClass: "w-full form-control reportrange-text",
  }),
  computed: {
    isRequired() {
      return this.rules?.search("required") > -1;
    },
    customRules() {
      return this.rules?.search("required") > -1 ? "datePickerRequire" : "";
    },
    dateRange: {
      get() {
        return {
          startDate: this.value,
          endDate: this.value,
        }
      },
      set(newValue) {
        this.onChange("input", newValue.startDate)
      }
    },
    rangeText () {
      let range = this.placeholder;

      if (this.dateRange.startDate)
      {
        range = this.formatDate(this.dateRange.startDate);
        if (!this.singleDatePicker || this.singleDatePicker === "range") {
          range += " - " + this.formatDate(this.dateRange.endDate);
        }
      }

      return range;
    },
    localeData() {
      return {
        firstDay: 1,
        format: this.format,
        daysOfWeek: [
          this.$t("Sun"),
          this.$t("Mon"),
          this.$t("Tue"),
          this.$t("Wed"),
          this.$t("Thu"),
          this.$t("Fri"),
          this.$t("Sat"),
        ],
        monthNames: [
          this.$t("Jan"),
          this.$t("Feb"),
          this.$t("Mar"),
          this.$t("Apr"),
          this.$t("May"),
          this.$t("Jun"),
          this.$t("Jul"),
          this.$t("Aug"),
          this.$t("Sep"),
          this.$t("Oct"),
          this.$t("Nov"),
          this.$t("Dec"),
        ],

      }
    },
    ranges() {
      let today = new Date()
      today.setHours(0, 0, 0, 0)

      let yesterday = new Date()
      yesterday.setDate(today.getDate() - 1)
      yesterday.setHours(0, 0, 0, 0);

      const thisMonthStart = new Date(this.$dayJs().startOf("month"));
      const thisMonthEnd = new Date(this.$dayJs().endOf("month"));
      return {
        [this.$t("Today")]: [today, today],
        [this.$t("Yesterday")]: [yesterday, yesterday],
        [this.$t("Last month")]: [new Date(today.getFullYear(), today.getMonth() - 1, 1), new Date(today.getFullYear(), today.getMonth(), 0)],
        [this.$t("This month")]: [thisMonthStart, thisMonthEnd],
        [this.$t("This year")]: [new Date(today.getFullYear(), 0, 1), new Date(today.getFullYear(), 11, 31)],
      }
    }
  },
  watch: {
    "dateRange.startDate": {
      handler(newValue) {
        this.placeHolderClass = "text-[#00000040]";
        if (!this.formatOnFirst || !newValue) return;
        if (newValue !== this.$dayJs(newValue).format("YYYY-MM-DD")) {
          this.onChange("input", newValue?.startDate)
        }

        this.placeHolderClass = "";
      },
      immediate: true
    }
  },
  methods: {
    onChange(event, newValue) {
      this.$emit(event, this.$dayJs(newValue).format("YYYY-MM-DD"));
    },
    todayFormatFn(classes, date) {
      const dt = new Date(date);
      dt.setHours(0, 0, 0, 0);

      const start = new Date(this.dateRange.startDate);
      start.setHours(0, 0, 0, 0);

      const end = new Date(this.dateRange.endDate);
      end.setHours(0, 0, 0, 0);

      classes["in-range"] = dt >= start && dt <= end;
      return classes;
    },
    dateFormatFn(classes, date) {
      classes = this.todayFormatFn(classes, date);

      const dt = dayjs(date).format("YYYY-MM-DD");
      const datesMarks = this.highlightDates.map(x => dayjs(x).format("YYYY-MM-DD"));

      if (datesMarks.includes(dt)) {
        classes.highlight = true;
      }
      return this.dateFormat ? this.dateFormat(classes, date) : classes;
    },
    formatDate(dateString) {
      const [year, month, day] = dateString.split("-");
      return `${ day }-${ month }-${ year }`;
    }
  },
  mounted() {
    if (this.disabled)
      this.controlContainerClass = this.controlContainerClass + " cursor-not-allowed bg-gray-300";
  }
}
</script>
<style lang="scss">
.vue-daterange-picker {
  @apply w-full;
}

.daterangepicker .monthselect {
  width: auto !important;;
}

.highlight {
  &:after {
    content: "*";
    color: red;
    font-size: 18px;
  }
}
</style>
